import { z } from "zod";

/**
 * Y1 TENANCY (decision rows 272, 296, 340): the per-client knobs as ONE versioned artifact, the "10%"
 * bespoke layer of the 90/10 model.
 *
 * Today these knobs are scattered: the AI disclosure choice and the notification routes live in
 * `clients.settings` (apps/web/src/lib/settings.ts), the automation on/off state is the latest
 * `automation.toggled` event per agent x scenario (apps/web/src/lib/automations.ts), and the timezone is
 * the new `clients.timezone` column. This schema is the SEAM: a `client_config` artifact version carries
 * all of them, reviewed and approved like any other artifact (T2, a human), and `readClientConfig`
 * (packages/db/src/clientConfig.ts) returns the active one or these defaults.
 *
 * THE DEFAULTS ARE TODAY'S BEHAVIOUR, not new policy: an owner who never wrote a config sees exactly what
 * the scattered readers show now. apps/web/src/lib/clientConfigDefaults.test.ts pins each default against
 * the reader that assumes it, so the two cannot drift apart unseen. The readers of `clients.settings` are
 * NOT migrated in this wave: the seam is the reader, and the cut-over goes key by key later.
 *
 * What does NOT belong here, ever (CLAUDE.md section 3, T3): guardrails, price and discount caps,
 * compliance rules, escalation triggers, SLA timers, the window state machine. The schema is strict, so
 * an unknown key (a discount cap, say) is refused rather than carried. The AI disclosure choice has
 * exactly two values and no denial, as in settings.ts (section 9).
 *
 * AWAITING RATIFICATION (Kabir): the notification categories and channels are the ones the settings page
 * already invented; this file copies them and a web test pins the copy.
 */

export const CLIENT_CONFIG_VERSION = 1 as const;

export const CONFIG_AI_DISCLOSURE_CHOICES = ["only_if_asked", "always_disclose"] as const;
export const CONFIG_NOTIFICATION_CATEGORIES = ["needs_you", "window_closing", "weekly_digest"] as const;
export const CONFIG_NOTIFICATION_CHANNELS = ["dashboard", "email"] as const;

/** The same shape `clients_timezone_check` (migration 0055) enforces, plus UTC. */
const TIMEZONE = z
  .string()
  .regex(/^(UTC|[A-Za-z_]+\/[A-Za-z0-9_+/-]+)$/, "a timezone is UTC or an IANA Area/Location name");

/** `<agent>:<scenario>`, the key the automations page toggles on. */
const AUTOMATION_KEY = z.string().regex(/^[a-z0-9-]+:[a-z0-9_]+$/, "an automation key is <agent>:<scenario>");

const channel = z.enum(CONFIG_NOTIFICATION_CHANNELS);

export const ClientConfigSchema = z.strictObject({
  schemaVersion: z.literal(CLIENT_CONFIG_VERSION),
  /** Section 9: honest when asked, or always up front. No value denies it. */
  aiDisclosure: z.enum(CONFIG_AI_DISCLOSURE_CHOICES),
  /** Where each notification category goes. */
  notificationRoutes: z.strictObject({
    needs_you: channel,
    window_closing: channel,
    weekly_digest: channel,
  }),
  /**
   * Explicit automation switches only. A pair with no entry is ON, as the automations page reads it today
   * (every automation runs, and a switch never lifts a draft gate: B6).
   */
  automations: z.record(AUTOMATION_KEY, z.boolean()),
  /** Mirror of `clients.timezone`; null is "not set". The worker reads the column itself (TA-1346, TA-1257). */
  timezone: TIMEZONE.nullable(),
});

export type ClientConfig = z.infer<typeof ClientConfigSchema>;

/** Today's behaviour, as a value. A fresh object each call, so a caller cannot mutate the shared default. */
export function defaultClientConfig(): ClientConfig {
  return {
    schemaVersion: CLIENT_CONFIG_VERSION,
    aiDisclosure: "only_if_asked",
    notificationRoutes: { needs_you: "dashboard", window_closing: "dashboard", weekly_digest: "dashboard" },
    automations: {},
    timezone: null,
  };
}

/** The stored content failed the schema. Its own class: a bad config is an operator's to read, never a default. */
export class ClientConfigInvalidError extends Error {
  readonly issues: readonly string[];
  constructor(issues: readonly string[]) {
    super(`the active client_config artifact does not match its schema: ${issues.join("; ")}`);
    this.name = "ClientConfigInvalidError";
    this.issues = issues;
  }
}

/**
 * Parses stored content. Strict and total: either a full `ClientConfig` or a `ClientConfigInvalidError`
 * naming the paths (never the values), because falling back to defaults on a malformed approved config would
 * silently drop an owner's choice (section 0, fail loudly).
 */
export function parseClientConfig(content: unknown): ClientConfig {
  const r = ClientConfigSchema.safeParse(content);
  if (r.success) return r.data;
  throw new ClientConfigInvalidError(r.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`));
}
